import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ProductList, { ProductListRef } from '../components/products/ProductList';
import { ProductDetailView } from '../components/products/ProductDetailView';
import { dashboardService } from '../services/firebase/DashboardService';
import { Product } from '../types/product';
import { Button } from '../components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import ImportDialog from '../components/dialogs/ImportDialog';
import BulkOptimizationDialog from '../components/products/BulkOptimizationDialog';

const ProductsPage = () => {
  const navigate = useNavigate();
  const { productId } = useParams();
  const [products, setProducts] = useState<Product[]>([]);
  const productListRef = useRef<ProductListRef>(null);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(productId || null);
  const [isLoading, setIsLoading] = useState(true);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [showBulkOptimizeDialog, setShowBulkOptimizeDialog] = useState(false);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const productData = await dashboardService.getProducts();
        console.log('Fetched Products:', productData.map(p => ({
          id: p.id,
          source: p.source,
          store: p.storeName,
          title: p.basicInfo.title
        })));
        setProducts(productData);
      } catch (error) {
        console.error('Failed to load products:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProducts();
  }, []);

  useEffect(() => {
    setSelectedProductId(productId || null);
  }, [productId]);

  const handleProductSelect = (productId: string) => {
    setSelectedProductId(productId);
    navigate(`/lms/products/${productId}`);
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      const productData = await dashboardService.getProducts();
      setProducts(productData);
    } catch (error) {
      console.error('Failed to refresh products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div className="p-8">Loading products...</div>;
  }

  const selectedProduct = products.find(p => p.id === selectedProductId);

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Products</h1>
          <div className="flex gap-2 w-full sm:w-auto">
            {!selectedProduct && (
              <>
                <Button
                  onClick={() => setShowImportDialog(true)}
                  className="bg-blue-600 hover:bg-blue-700 flex items-center gap-1 py-1.5 h-auto text-sm w-full sm:w-auto"
                >
                  <span>+</span> Import Products
                </Button>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Button
                        onClick={() => setShowBulkOptimizeDialog(true)}
                        className="bg-green-600 hover:bg-green-700 flex items-center gap-1 py-1.5 h-auto text-sm w-full sm:w-auto"
                        disabled={selectedProducts.length === 0}
                      >
                        Bulk Optimize ({selectedProducts.length})
                      </Button>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-gray-800 text-white px-3 py-2 text-sm rounded-md">
                    Select products to optimize
                  </TooltipContent>
                </Tooltip>
              </>
            )}
          </div>
        </div>

        <ImportDialog
          isOpen={showImportDialog}
          onOpenChange={setShowImportDialog}
          onImportComplete={handleRefresh}
        />

        <BulkOptimizationDialog
          isOpen={showBulkOptimizeDialog}
          onOpenChange={setShowBulkOptimizeDialog}
          products={selectedProducts}
          onComplete={handleRefresh}
          onClearSelection={() => {
            setSelectedProducts([]);
            productListRef.current?.clearSelections();
          }}
        />

        {selectedProduct ? (
          <ProductDetailView 
            product={selectedProduct} 
            onRefresh={handleRefresh}
          />
        ) : (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <ProductList
                ref={productListRef}
                products={products}
                onSelectProduct={handleProductSelect}
                selectedProductId={selectedProductId}
                maxHeight="calc(100vh - 300px)"
                onRefresh={handleRefresh}
                compact={false}
                showSearch={true}
                showFilters={true}
                onSelectionChange={(selectedIds) => setSelectedProducts(
                  products.filter(p => selectedIds.includes(p.id))
                )}
                showCheckboxes={true}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
